import React, { useState, useEffect } from "react";
import contractService from "../../services/contractService";
import { formatAmount } from "../../utils/formatters";

const FilterPanel = ({
  onFilterChange,
  initialFilters = {},
  totals = {},
  generalTotals = {},
  isRestoring = false, // NOUVEAU: Prop pour indiquer si on est en restauration
}) => {
  const [filterOptions, setFilterOptions] = useState({
    agencies: [],
    years: [],
    months: [],
  });

  const [filters, setFilters] = useState({
    agency: initialFilters.agency || "",
    year: initialFilters.year || new Date().getFullYear().toString(),
    month: initialFilters.month || "",
    search: initialFilters.search || "",
    statusFilter: initialFilters.statusFilter || "", // 🆕 NOUVEAU: Filtre par statut
  });

  // 🆕 NOUVEAU: État pour les boutons de filtrage avancé
  const [activeStatusFilter, setActiveStatusFilter] = useState(
    initialFilters.statusFilter || ""
  );

  // MODIFICATION: Charger l'état depuis localStorage, par défaut true (étendue)
  const [expanded, setExpanded] = useState(() => {
    const saved = localStorage.getItem("sidebar_expanded");
    return saved !== null ? saved === "true" : true;
  });

  // 🆕 NOUVEAU: État pour contrôler l'affichage réduit/étendu des totaux généraux
  const [totalsExpanded, setTotalsExpanded] = useState(false);

  // Fonction pour formater les nombres décimaux avec 2 décimales
  const formatDecimal = (value) => {
    if (value === null || value === undefined) return "";
    return Number(parseFloat(value).toFixed(2)).toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // MODIFICATION: Charger les options de filtres avec condition de restauration
  useEffect(() => {
    // Ne pas recharger les options si on est en cours de restauration
    if (isRestoring) {
      console.log(
        "⏸️ Report du chargement des options de filtres (restauration en cours)"
      );
      return;
    }

    const loadFilterOptions = async () => {
      try {
        const result = await contractService.getFilterOptions();
        if (result.success) {
          setFilterOptions(result.data);
          console.log("Options de filtres chargées:", result.data);
        }
      } catch (error) {
        console.error(
          "Erreur lors du chargement des options de filtres:",
          error
        );
      }
    };

    loadFilterOptions();
  }, [isRestoring]); // MODIFICATION: Ajouter isRestoring aux dépendances

  // AJOUT: Effet pour ajuster la marge au montage selon l'état de la sidebar
  useEffect(() => {
    const contentElement = document.querySelector(".content-with-sidebar");
    if (contentElement) {
      if (expanded) {
        contentElement.style.marginLeft = "16rem";
      } else {
        contentElement.style.marginLeft = "1rem";
      }
    }
  }, [expanded]);

  // MODIFICATION: Mettre à jour les filtres avec condition de restauration
  useEffect(() => {
    // Ne pas mettre à jour automatiquement pendant la restauration
    if (isRestoring) {
      console.log(
        "⏸️ Report de la mise à jour des filtres (restauration en cours)"
      );
      return;
    }

    setFilters({
      agency: initialFilters.agency || "",
      year: initialFilters.year || "",
      month: initialFilters.month || "",
      search: initialFilters.search || "",
      statusFilter: initialFilters.statusFilter || "", // 🆕 NOUVEAU
    });

    // 🆕 NOUVEAU: Mettre à jour l'état actif du filtre de statut
    setActiveStatusFilter(initialFilters.statusFilter || "");
  }, [initialFilters, isRestoring]); // MODIFICATION: Ajouter isRestoring

  // Gérer les changements des filtres
  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // 🆕 NOUVEAU: Gérer les clics sur les boutons de filtrage avancé
  const handleStatusFilterClick = (statusType) => {
    const newFilters = { ...filters };

    if (activeStatusFilter === statusType) {
      // Si le bouton est déjà actif, le désactiver
      newFilters.statusFilter = "";
      setActiveStatusFilter("");
    } else {
      // Activer le nouveau filtre
      newFilters.statusFilter = statusType;
      setActiveStatusFilter(statusType);
    }

    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  // Appliquer les filtres
  const handleApplyFilters = () => {
    onFilterChange(filters);
  };

  // Réinitialiser les filtres
  const handleResetFilters = () => {
    const currentDate = new Date();
    const defaultFilters = {
      agency: "",
      year: "",
      month: currentDate.getMonth() + 1,
      search: "",
      statusFilter: "", // 🆕 NOUVEAU: Réinitialiser le filtre de statut
    };

    setFilters(defaultFilters);
    setActiveStatusFilter(""); // 🆕 NOUVEAU: Réinitialiser l'état actif
    onFilterChange(defaultFilters);
  };

  // MODIFICATION: Basculer l'affichage étendu/compact de la sidebar
  const toggleExpanded = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);

    // Sauvegarder l'état dans localStorage
    localStorage.setItem("sidebar_expanded", newExpanded.toString());

    // Mettre à jour la classe CSS du corps principal pour ajuster les marges
    const contentElement = document.querySelector(".content-with-sidebar");
    if (contentElement) {
      if (newExpanded) {
        contentElement.style.marginLeft = "16rem"; // 256px quand la sidebar est étendue
      } else {
        contentElement.style.marginLeft = "1rem"; // 16px quand la sidebar est réduite
      }
    }
  };

  return (
    <div
      className={`sidebar-filter fixed left-0 top-0 h-full bg-white shadow-lg transition-all duration-300 z-40 flex flex-col ${
        expanded ? "w-64" : "w-4"
      }`}
    >
      {/* Indicateur de restauration */}
      {isRestoring && expanded && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2">
          <div className="flex items-center text-blue-700 text-sm">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mr-2"></div>
            <span>Restauration en cours...</span>
          </div>
        </div>
      )}

      {/* Contenu de la sidebar */}
      <div
        className={`w-full ${
          expanded ? "px-4" : "px-1"
        } py-4 overflow-y-auto flex-grow`}
      >
        {expanded && (
          <h2 className="text-lg font-medium border-b pb-2 mb-4">Filtres</h2>
        )}

        {/* Agence */}
        <div className="mb-4">
          {expanded && (
            <div className="flex-grow">
               {/*<label
                htmlFor="agency"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Agence
              </label>*/}
              <select
                id="agency"
                name="agency"
                value={filters.agency}
                onChange={handleFilterChange}
                disabled={isRestoring} // NOUVEAU: Désactiver pendant la restauration
                className={`bg-blue-50 border p-3 block w-full pl-3 pr-10 py-1.5 text-sm border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 rounded-md ${
                  isRestoring ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <option value="">Toutes les agences</option>
                {filterOptions.agencies.map((agency) => (
                  <option key={agency} value={agency}>
                    {agency}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Année */}
        <div className="mb-4">
          {expanded && (
            <div className="flex-grow">
              {/*<label
                htmlFor="year"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Année
              </label>*/}
              <select
                id="year"
                name="year"
                value={filters.year}
                onChange={handleFilterChange}
                disabled={isRestoring} // NOUVEAU: Désactiver pendant la restauration
                className={`bg-green-50 border p-3         block w-full pl-3 pr-10 py-1.5 text-sm border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 rounded-md ${
                  isRestoring ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <option value="">Toutes les années</option>
                {filterOptions.years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Mois */}
        <div className="mb-4">
          {expanded && (
            <div className="flex-grow">
             {/*<label
                htmlFor="month"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Mois
              </label>*/}
              <select
                id="month"
                name="month"
                value={filters.month}
                onChange={handleFilterChange}
                disabled={isRestoring} // NOUVEAU: Désactiver pendant la restauration
                className={`bg-orange-50 border p-3         block w-full pl-3 pr-10 py-1.5 text-sm border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 rounded-md ${
                  isRestoring ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <option value="">Tous les mois</option>
                {filterOptions.months.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Recherche */}
        <div className="mb-6">
          {expanded && (
            <div className="flex-grow">
              {/*<label
                htmlFor="search"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Recherche
              </label>*/}
              <input
                type="text"
                id="search"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="🔍: Contrat, client, téléphone..."
                disabled={isRestoring} // NOUVEAU: Désactiver pendant la restauration
                className={`bg-purple-50 border p-3       block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm py-1.5 ${
                  isRestoring ? "opacity-50 cursor-not-allowed" : ""
                }`}
              />
            </div>
          )}
        </div>

        {/* Boutons d'action */}
        {expanded && (
          <div className="mt-auto border-t pt-4">
            <div className="flex flex-col space-y-2">
              <button
                type="button"
                onClick={handleResetFilters}
                disabled={isRestoring} // NOUVEAU: Désactiver pendant la restauration
                className={`flex items-center justify-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors ${
                  isRestoring ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                Réinitialiser
              </button>
              <button
                type="button"
                onClick={handleApplyFilters}
                disabled={isRestoring} // NOUVEAU: Désactiver pendant la restauration
                className={`flex items-center justify-center px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors ${
                  isRestoring ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                Appliquer
              </button>
            </div>
            {/* 🆕 NOUVEAU : Boutons de filtrage avancé */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Filtres rapides
              </h3>
              <div className="flex flex-col space-y-2">
                {/* Bouton Dépassement - CORRIGÉ */}
                <button
                  type="button"
                  onClick={() => handleStatusFilterClick("depassement")}
                  disabled={isRestoring}
                  className={`flex items-center justify-center px-3 py-2 text-xs font-medium rounded-md transition-all duration-200 ${
                    activeStatusFilter === "depassement"
                      ? "bg-red-600 text-white shadow-md ring-2 ring-red-300"
                      : "bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
                  } ${
                    isRestoring
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                  title="Afficher uniquement les contrats en dépassement d'échéance (non vendus)"
                >
                  <span className="mr-1">⚠️</span>
                  <span className="hidden sm:inline">Dépassement</span>
                  <span className="sm:hidden">Dép.</span>
                  {activeStatusFilter === "depassement" && (
                    <span className="ml-1 text-white">✓</span>
                  )}
                </button>

                {/* Bouton Vendu - INCHANGÉ */}
                <button
                  type="button"
                  onClick={() => handleStatusFilterClick("vendu")}
                  disabled={isRestoring}
                  className={`flex items-center justify-center px-3 py-2 text-xs font-medium rounded-md transition-all duration-200 ${
                    activeStatusFilter === "vendu"
                      ? "bg-green-600 text-white shadow-md ring-2 ring-green-300"
                      : "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
                  } ${
                    isRestoring
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                  title="Afficher uniquement les contrats vendus"
                >
                  <span className="mr-1">💰</span>
                  <span className="hidden sm:inline">Vendus</span>
                  <span className="sm:hidden">Vend.</span>
                  {activeStatusFilter === "vendu" && (
                    <span className="ml-1 text-white">✓</span>
                  )}
                </button>

                {/* Bouton Recouvrements - NOUVEAU */}
                <button
                  type="button"
                  onClick={() => handleStatusFilterClick("en_cours")}
                  disabled={isRestoring}
                  className={`flex items-center justify-center px-3 py-2 text-xs font-medium rounded-md transition-all duration-200 ${
                    activeStatusFilter === "en_cours"
                      ? "bg-blue-600 text-white shadow-md ring-2 ring-blue-300"
                      : "bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"
                  } ${
                    isRestoring
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                  title="Afficher uniquement les contrats en cours (non rachetés)"
                >
                  <span className="mr-1">🔄</span>
                  <span className="hidden sm:inline">Recouvrements</span>
                  <span className="sm:hidden">Recouv.</span>
                  {activeStatusFilter === "en_cours" && (
                    <span className="ml-1 text-white">✓</span>
                  )}
                </button>
              </div>

              {/* Indicateur du filtre actif - AMÉLIORÉ */}
              {activeStatusFilter && (
                <div className="mt-2 p-2 bg-blue-50 rounded-md">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-blue-700">
                      Filtre actif:{" "}
                      {activeStatusFilter === "depassement"
                        ? "⚠️ Dépassement (non vendus)"
                        : activeStatusFilter === "vendu"
                        ? "💰 Vendus"
                        : "🔄 Recouvrements (en cours)"}
                    </span>
                    <button
                      onClick={() =>
                        handleStatusFilterClick(activeStatusFilter)
                      }
                      className="text-blue-500 hover:text-blue-700 text-xs"
                      title="Désactiver le filtre"
                    >
                      ✕
                    </button>
                  </div>

                  {/* 🆕 NOUVEAU : Description explicative */}
                  <div className="mt-1 text-xs text-gray-600">
                    {activeStatusFilter === "depassement"
                      ? "Contrats avec date limite dépassée, non clôturés et non vendus"
                      : activeStatusFilter === "vendu"
                      ? "Contrats marqués comme vendus dans les remarques"
                      : "Contrats en cours qui n'ont pas encore été rachetés (clôturés)"}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Affichage des totaux en bas de la sidebar */}
      {expanded && Object.keys(generalTotals).length > 0 && (
        <div className="w-full px-4 py-3 mt-auto bg-gray-100 border-t border-gray-300">
          <h3 
            className="font-medium text-sm mb-2 text-gray-700 cursor-pointer flex justify-between items-center hover:text-blue-600 transition-colors"
            onClick={() => setTotalsExpanded(!totalsExpanded)}
            title="Cliquer pour afficher ou masquer les totaux"
          >
            <div className="flex items-center">
              Totaux généraux
              {isRestoring && (
                <span className="ml-2 text-blue-600 text-xs">🔄</span>
              )}
              {/* 🆕 NOUVEAU : Indicateur de filtre actif dans les totaux */}
              {activeStatusFilter && (
                <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                  {activeStatusFilter === "depassement"
                    ? "Dépassement"
                    : activeStatusFilter === "vendu"
                    ? "Vendus"
                    : "Recouvrements"}
                </span>
              )}
            </div>
            <span className="text-gray-400 text-xs">{totalsExpanded ? "▼" : "▶"}</span>
          </h3>
          
          {/* Contenu rétractable */}
          <div
            className={`space-y-1 text-xs transition-all overflow-hidden ${
              totalsExpanded ? "max-h-64 opacity-100 mt-2" : "max-h-0 opacity-0 m-0"
            } ${isRestoring ? "opacity-50" : ""}`}
          >
            <div className="flex justify-between">
              <span className="text-gray-600">Contrats:</span>
              <span className="font-medium">
                {generalTotals.totalCount || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Poids bijou:</span>
              <span className="font-medium">
                {formatDecimal(generalTotals.jewelWeight)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">CDVA:</span>
              <span className="font-medium">
                {formatDecimal(generalTotals.cdvaWeight)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">CDD:</span>
              <span className="font-medium">
                {formatDecimal(generalTotals.cddWeight)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Décaissement:</span>
              <span className="font-medium">
                {formatAmount(generalTotals.disbursementAmount)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Prévu:</span>
              <span className="font-medium">
                {formatAmount(generalTotals.expectedAmount)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Perçu:</span>
              <span className="font-medium">
                {formatAmount(generalTotals.receivedAmount)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Reste:</span>
              <span className="font-medium">
                {formatAmount(generalTotals.remainingAmount)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Remise:</span>
              <span className="font-medium">
                {generalTotals.discount > 0
                  ? formatAmount(generalTotals.discount)
                  : "-"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Bouton de contrôle de la sidebar */}
      <button
        onClick={toggleExpanded}
        disabled={isRestoring} // NOUVEAU: Désactiver pendant la restauration
        className={`absolute -right-3 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-1 shadow-md border border-gray-200 z-50 transition-opacity ${
          isRestoring ? "opacity-50 cursor-not-allowed" : ""
        }`}
        title={
          isRestoring
            ? "Restauration en cours..."
            : expanded
            ? "Réduire la sidebar"
            : "Étendre la sidebar"
        }
      >
        {expanded ? "◀" : "▶"}
      </button>
    </div>
  );
};

export default FilterPanel;
